/** Saved stage-specific display evidence; never changes a positive decision gate.
 * Currency remains disclosed by the host. A readable saved body can remain
 * visible during refresh; a held, private, incomplete or unrelated body cannot. */
import { reportTypeStage, type DecisionStage } from '../../config/reportTypes';
import { reportCanContributeSavedResult, type ReportPlanItem } from '../pathToGo/reportPlan';
import { analysisReadinessDecisionV1Schema } from '../../types/decisionAnalysis';
import type { SiteRagReport } from '../../types/siteRag';

export function selectDecisionStageReports(
  reports: readonly SiteRagReport[],
  stage: DecisionStage,
  reportPlan?: readonly ReportPlanItem[] | null,
  siteId?: string,
): SiteRagReport[] {
  return reports.filter(report => {
    const type = report.reportType ?? 'feasibility';
    if (siteId != null && report.siteId != null && report.siteId !== siteId) return false;
    if (reportTypeStage(type) !== stage) return false;
    if (reportPlan && !reportPlan.some(item => item.type === type && item.role !== 'optional')) return false;
    if (!reportCanContributeSavedResult(report, type)) return false;
    if (report.qualityHold === 'needs-revision' || (report.qualityHoldFindings?.length ?? 0) > 0) return false;
    const raw = report.analysisReadiness ?? report.analysisRevision?.readiness;
    if (raw == null) return true;
    const parsed = analysisReadinessDecisionV1Schema.safeParse(raw);
    return parsed.success && parsed.data.canView && parsed.data.identityValid
      && parsed.data.qualityReady && parsed.data.narrativeReady
      && ((parsed.data.ready && parsed.data.canUseForDecision)
        || parsed.data.state === 'queued' || parsed.data.state === 'refreshing');
  });
}
